/**
 * Learning Paths Dashboard Slice
 *
 * Redux state management for the coach learning paths dashboard.
 * Handles path listings, detail views, company progress, and enrollment management.
 */

import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/rootReducer";
import type {
  CoachLearningPathsDashboardState,
  CoachLearningPathSummary,
  CompanyPathProgress,
  DashboardFilters,
  GetCoachPathsRequest,
  GetPathDetailRequest,
  GetCompanyPathProgressRequest,
} from "../types";
import {
  createEmptyDashboardFilters,
  createInitialDashboardState,
} from "../types";
import {
  getCoachLearningPaths,
  getCoachPathDetail,
  getCompanyPathProgress,
  getLearners,
  GetLearnersRequest,
} from "@/api/coaching/getCoachLearningPaths";

// ============================================================================
// Initial State
// ============================================================================

const initialState: CoachLearningPathsDashboardState =
  createInitialDashboardState();

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch learning paths for the coach dashboard
 */
export const fetchCoachPaths = createAsyncThunk(
  "learningPathsDashboard/fetchCoachPaths",
  async (params: GetCoachPathsRequest | undefined, { rejectWithValue }) => {
    try {
      const response = await getCoachLearningPaths(params);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch learning paths";
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Fetch detailed information for a specific learning path
 */
export const fetchPathDetail = createAsyncThunk(
  "learningPathsDashboard/fetchPathDetail",
  async (params: GetPathDetailRequest, { rejectWithValue }) => {
    try {
      const response = await getCoachPathDetail(params);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch path details";
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Fetch company-level progress aggregation
 */
export const fetchCompanyProgress = createAsyncThunk(
  "learningPathsDashboard/fetchCompanyProgress",
  async (
    params: GetCompanyPathProgressRequest | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await getCompanyPathProgress(params);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch company progress";
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Fetch learners across all paths
 */
export const fetchLearners = createAsyncThunk(
  "learningPathsDashboard/fetchLearners",
  async (params: GetLearnersRequest | undefined, { rejectWithValue }) => {
    try {
      const response = await getLearners(params);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch learners";
      return rejectWithValue(errorMessage);
    }
  },
);

// ============================================================================
// Slice Definition
// ============================================================================

const learningPathsDashboardSlice = createSlice({
  name: "learningPathsDashboard",
  initialState,
  reducers: {
    // -------------------------------------------------------------------------
    // Path Selection
    // -------------------------------------------------------------------------

    /**
     * Set the currently selected path ID
     */
    setSelectedPathId: (state, action: PayloadAction<number | null>) => {
      state.selectedPathId = action.payload;
      // Clear detail when deselecting
      if (action.payload === null) {
        state.selectedPathDetail = null;
      }
    },

    /**
     * Clear selected path and its detail
     */
    clearSelectedPath: (state) => {
      state.selectedPathId = null;
      state.selectedPathDetail = null;
      state.selectedPathLoading = false;
      state.selectedPathError = null;
    },

    // -------------------------------------------------------------------------
    // View Mode
    // -------------------------------------------------------------------------

    /**
     * Set the dashboard view mode
     */
    setViewMode: (
      state,
      action: PayloadAction<"paths" | "companies" | "learners" | "engagement">,
    ) => {
      state.viewMode = action.payload;
    },

    // -------------------------------------------------------------------------
    // Filters
    // -------------------------------------------------------------------------

    /**
     * Update dashboard filters (partial update)
     */
    setFilters: (state, action: PayloadAction<Partial<DashboardFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset pagination when filters change
      state.pagination.page = 1;
    },

    /**
     * Set search query filter
     */
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
      state.pagination.page = 1;
    },

    /**
     * Set status filter
     */
    setStatusFilter: (
      state,
      action: PayloadAction<DashboardFilters["statusFilter"]>,
    ) => {
      state.filters.statusFilter = action.payload;
      state.pagination.page = 1;
    },

    /**
     * Set sort options
     */
    setSortOptions: (
      state,
      action: PayloadAction<{
        sortBy: DashboardFilters["sortBy"];
        sortDirection: DashboardFilters["sortDirection"];
      }>,
    ) => {
      state.filters.sortBy = action.payload.sortBy;
      state.filters.sortDirection = action.payload.sortDirection;
    },

    /**
     * Set company IDs filter
     */
    setCompanyFilter: (state, action: PayloadAction<number[]>) => {
      state.filters.companyIds = action.payload;
      state.pagination.page = 1;
    },

    /**
     * Set path IDs filter (server-side learners filter)
     */
    setPathFilter: (state, action: PayloadAction<number[]>) => {
      state.filters.pathIds = action.payload;
      state.pagination.page = 1;
    },

    /**
     * Reset all filters to default values
     */
    resetFilters: (state) => {
      state.filters = createEmptyDashboardFilters();
      state.pagination.page = 1;
    },

    // -------------------------------------------------------------------------
    // Pagination
    // -------------------------------------------------------------------------

    /**
     * Set current page
     */
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },

    /**
     * Set page size
     */
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 1; // Reset to first page
    },

    // -------------------------------------------------------------------------
    // Error Handling
    // -------------------------------------------------------------------------

    /**
     * Clear all errors
     */
    clearErrors: (state) => {
      state.pathsError = null;
      state.selectedPathError = null;
      state.companyProgressError = null;
    },

    /**
     * Clear paths error only
     */
    clearPathsError: (state) => {
      state.pathsError = null;
    },

    /**
     * Clear selected path error only
     */
    clearSelectedPathError: (state) => {
      state.selectedPathError = null;
    },

    /**
     * Clear company progress error only
     */
    clearCompanyProgressError: (state) => {
      state.companyProgressError = null;
    },

    // -------------------------------------------------------------------------
    // State Reset
    // -------------------------------------------------------------------------

    /**
     * Reset entire dashboard state to initial values
     */
    resetDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    // -------------------------------------------------------------------------
    // Fetch Coach Paths
    // -------------------------------------------------------------------------
    builder
      .addCase(fetchCoachPaths.pending, (state) => {
        state.pathsLoading = true;
        state.pathsError = null;
      })
      .addCase(fetchCoachPaths.fulfilled, (state, action) => {
        state.pathsLoading = false;
        state.paths = action.payload.paths;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCoachPaths.rejected, (state, action) => {
        state.pathsLoading = false;
        state.pathsError = action.payload as string;
      });

    // -------------------------------------------------------------------------
    // Fetch Path Detail
    // -------------------------------------------------------------------------
    builder
      .addCase(fetchPathDetail.pending, (state) => {
        state.selectedPathLoading = true;
        state.selectedPathError = null;
      })
      .addCase(fetchPathDetail.fulfilled, (state, action) => {
        state.selectedPathLoading = false;
        state.selectedPathDetail = action.payload;
        if (action.payload) {
          state.selectedPathId = action.payload.id;
        }
      })
      .addCase(fetchPathDetail.rejected, (state, action) => {
        state.selectedPathLoading = false;
        state.selectedPathError = action.payload as string;
      });

    // -------------------------------------------------------------------------
    // Fetch Company Progress
    // -------------------------------------------------------------------------
    builder
      .addCase(fetchCompanyProgress.pending, (state) => {
        state.companyProgressLoading = true;
        state.companyProgressError = null;
      })
      .addCase(fetchCompanyProgress.fulfilled, (state, action) => {
        state.companyProgressLoading = false;
        state.companyProgress = action.payload.companies;
      })
      .addCase(fetchCompanyProgress.rejected, (state, action) => {
        state.companyProgressLoading = false;
        state.companyProgressError = action.payload as string;
      });

    // -------------------------------------------------------------------------
    // Fetch Learners
    // -------------------------------------------------------------------------
    builder
      .addCase(fetchLearners.pending, (state) => {
        state.learnersLoading = true;
        state.learnersError = null;
      })
      .addCase(fetchLearners.fulfilled, (state, action) => {
        state.learnersLoading = false;
        state.learners = action.payload.learners;
        state.learnersPagination = action.payload.pagination;
      })
      .addCase(fetchLearners.rejected, (state, action) => {
        state.learnersLoading = false;
        state.learnersError = action.payload as string;
      });
  },
});

// ============================================================================
// Export Actions
// ============================================================================

export const {
  // Path selection
  setSelectedPathId,
  clearSelectedPath,

  // View mode
  setViewMode,

  // Filters
  setFilters,
  setSearchQuery,
  setStatusFilter,
  setSortOptions,
  setCompanyFilter,
  setPathFilter,
  resetFilters,

  // Pagination
  setPage,
  setPageSize,

  // Error handling
  clearErrors,
  clearPathsError,
  clearSelectedPathError,
  clearCompanyProgressError,

  // State reset
  resetDashboard,
} = learningPathsDashboardSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select all paths from the dashboard
 */
export const selectPaths = (state: RootState) =>
  state.learningPathsDashboard.paths;

/**
 * Select paths loading state
 */
export const selectPathsLoading = (state: RootState) =>
  state.learningPathsDashboard.pathsLoading;

/**
 * Select paths error
 */
export const selectPathsError = (state: RootState) =>
  state.learningPathsDashboard.pathsError;

/**
 * Select currently selected path ID
 */
export const selectSelectedPathId = (state: RootState) =>
  state.learningPathsDashboard.selectedPathId;

/**
 * Select selected path detail
 */
export const selectSelectedPathDetail = (state: RootState) =>
  state.learningPathsDashboard.selectedPathDetail;

/**
 * Select selected path loading state
 */
export const selectSelectedPathLoading = (state: RootState) =>
  state.learningPathsDashboard.selectedPathLoading;

/**
 * Select selected path error
 */
export const selectSelectedPathError = (state: RootState) =>
  state.learningPathsDashboard.selectedPathError;

/**
 * Select company progress data
 */
export const selectCompanyProgress = (state: RootState) =>
  state.learningPathsDashboard.companyProgress;

/**
 * Select company progress loading state
 */
export const selectCompanyProgressLoading = (state: RootState) =>
  state.learningPathsDashboard.companyProgressLoading;

/**
 * Select company progress error
 */
export const selectCompanyProgressError = (state: RootState) =>
  state.learningPathsDashboard.companyProgressError;

/**
 * Select learners data
 */
export const selectLearners = (state: RootState) =>
  state.learningPathsDashboard.learners;

/**
 * Select learners loading state
 */
export const selectLearnersLoading = (state: RootState) =>
  state.learningPathsDashboard.learnersLoading;

/**
 * Select learners error
 */
export const selectLearnersError = (state: RootState) =>
  state.learningPathsDashboard.learnersError;

/**
 * Select learners pagination
 */
export const selectLearnersPagination = (state: RootState) =>
  state.learningPathsDashboard.learnersPagination;

/**
 * Select current filters
 */
export const selectFilters = (state: RootState) =>
  state.learningPathsDashboard.filters;

/**
 * Select current view mode
 */
export const selectViewMode = (state: RootState) =>
  state.learningPathsDashboard.viewMode;

/**
 * Select pagination info
 */
export const selectPagination = (state: RootState) =>
  state.learningPathsDashboard.pagination;

/**
 * Select if any loading is in progress
 */
export const selectIsLoading = (state: RootState) =>
  state.learningPathsDashboard.pathsLoading ||
  state.learningPathsDashboard.selectedPathLoading ||
  state.learningPathsDashboard.companyProgressLoading;

/**
 * Select if there are any errors
 */
export const selectHasErrors = (state: RootState) =>
  !!(
    state.learningPathsDashboard.pathsError ||
    state.learningPathsDashboard.selectedPathError ||
    state.learningPathsDashboard.companyProgressError
  );

/**
 * Select paths filtered by status (memoized derived selector)
 */
export const selectFilteredPaths = createSelector(
  [
    (state: RootState) => state.learningPathsDashboard.paths,
    (state: RootState) => state.learningPathsDashboard.filters.statusFilter,
    (state: RootState) => state.learningPathsDashboard.filters.searchQuery,
  ],
  (paths, statusFilter, searchQuery) => {
    let filtered = paths;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (path) =>
          path.name.toLowerCase().includes(query) ||
          path.description?.toLowerCase().includes(query),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((path) => {
        switch (statusFilter) {
          case "active":
            return path.status === "published" && path.activeEnrollments > 0;
          case "completed":
            return (
              path.completedEnrollments === path.totalEnrollments &&
              path.totalEnrollments > 0
            );
          case "needsAttention":
            return (
              path.averageCompletionRate < 50 && path.activeEnrollments > 0
            );
          default:
            return true;
        }
      });
    }

    return filtered;
  },
);

/**
 * Select enrollments for the selected path
 */
export const selectSelectedPathEnrollments = (state: RootState) =>
  state.learningPathsDashboard.selectedPathDetail?.enrollments || [];

/**
 * Select path statistics for the selected path
 */
export const selectSelectedPathStatistics = (state: RootState) =>
  state.learningPathsDashboard.selectedPathDetail?.statistics || null;

/**
 * Select path by ID from the paths list
 */
export const selectPathById =
  (pathId: number) =>
  (state: RootState): CoachLearningPathSummary | undefined =>
    state.learningPathsDashboard.paths.find((p) => p.id === pathId);

/**
 * Select company progress by company ID
 */
export const selectCompanyProgressById =
  (companyId: number) =>
  (state: RootState): CompanyPathProgress | undefined =>
    state.learningPathsDashboard.companyProgress.find(
      (c) => c.companyId === companyId,
    );

// ============================================================================
// Export Reducer
// ============================================================================

export default learningPathsDashboardSlice.reducer;
